import iftttAiLogo from "images/logo-ifttt-ai.svg?raw"
import iftttAiLogoMono from "images/logo-ifttt-ai-mono.svg?raw"
import iftttWordmark from "images/logo-ifttt.svg?raw"
import iftttWordmarkWhite from "images/logo-ifttt-white.svg?raw"

export type IftttAiLogoVariant = "color" | "mono"
export type IftttAiLogoBackground = "light" | "dark"

interface IftttAiLogoOptions {
  variant?: IftttAiLogoVariant
  on?: IftttAiLogoBackground
  ai?: boolean
  className?: string
}

const SOURCES: Record<IftttAiLogoVariant, string> = { color: iftttAiLogo, mono: iftttAiLogoMono }
// Width of the IFTTT glyphs in the 394×80 viewBox, for the logo next to a plan badge.
const IFTTT_ONLY_VIEWBOX_WIDTH: Record<IftttAiLogoVariant, number> = { color: 296.6, mono: 286.41 }
// The CI svgo step merges a one-path group into its path, so ".ai" can be a <g> or a <path>.
const AI_ELEMENT = /<g class="ai"[^>]*>[\s\S]*?<\/g>|<path[^>]*\sclass="ai"[^>]*\/>/

export const iftttAiLaunch = (): boolean => !!window.App?.iftttAiLaunch

// Mirrors IftttLogoHelper#ifttt_ai_logo_svg. Colors come from foundation/_ifttt_logo.scss.
export const iftttAiLogoSvg = ({
  variant = "color",
  on = "light",
  ai = true,
  className,
}: IftttAiLogoOptions = {}): string => {
  const rootClass = variant === "mono" ? "ifttt-logo-ai-mono" : "ifttt-logo-ai"
  let svg = SOURCES[variant]

  if (!ai) {
    const width = IFTTT_ONLY_VIEWBOX_WIDTH[variant]
    svg = svg
      .replace(AI_ELEMENT, "")
      .replace('width="394"', `width="${width}"`)
      .replace('viewBox="0 0 394 80"', `viewBox="0 0 ${width} 80"`)
  }

  const classes = [rootClass, `${rootClass}-on-${on}`, className].filter(Boolean).join(" ")
  return svg
    .replace(` class="${rootClass}"`, "")
    .replace("<svg ", `<svg class="${classes}" role="img" `)
    .replace(/\saria-label="[^"]*"/, ` aria-label="${ai ? "IFTTT.ai" : "IFTTT"}"`)
}

const oldWordmark = (on: IftttAiLogoBackground) => (on === "dark" ? iftttWordmarkWhite : iftttWordmark)

// The standalone brand logo: IFTTT.ai after the launch, the old wordmark before. On a dark
// or brand-color surface it defaults to mono white, the family of the old white wordmark.
export const brandLogoSvg = ({
  on = "light",
  variant = on === "dark" ? "mono" : "color",
  launched = iftttAiLaunch(),
}: { on?: IftttAiLogoBackground; variant?: IftttAiLogoVariant; launched?: boolean } = {}): string =>
  launched ? iftttAiLogoSvg({ variant, on }) : oldWordmark(on)

// The logo next to a plan badge: IFTTT without the ".ai" after the launch, the old wordmark
// before. Full color on both backgrounds, as in the home upsell frame.
export const lockupLogoSvg = (on: IftttAiLogoBackground = "light", launched = iftttAiLaunch()): string =>
  launched ? iftttAiLogoSvg({ on, ai: false }) : oldWordmark(on)
